// Онбординг Mini App (HQ-1092 → HQ-2401, Figma wu4Ampg6ZfxaADl9RPw95o): когда показывать, шаги по списку сервера, отметки
// «пройден / пропущен» и «политика принята», события воронки. Чистая логика без React; вид — OnboardingScreen и его шаги.
// Порядок шагов — flowModel.ts. Модуль без строк интерфейса (i18n): его читают ворота App и тур при старте.
import { track } from '../../analytics/track';
import type { PickStep } from '../../api/onboarding';
import { favoritesCount, type FavoritesSet } from '../../session/favorites';

/** Маршрут онбординга: имя экрана в аналитике — onboarding_setup (шаблон ^onboarding_ словаря screen_name). */
export const ONBOARDING_ROUTE = '/onboarding/setup';
export const MARK_KEY = 'cricket.onboarding';
/** done — пройден до конца, skipped — пропущен целиком, returning — избранное уже было (вернувшийся человек). */
export type OnboardingMark = 'done' | 'skipped' | 'returning';
const MARKS: readonly string[] = ['done', 'skipped', 'returning'];
/** flow_source событий: онбординг первого запуска (у мягкой карточки HQ-726 — home_card). */
export const FLOW_SOURCE = 'first_launch';
export const PAYWALL_SOURCE = 'onboarding';
/** Сколько ждём поток сервера, прежде чем рисовать встроенный порядок. */
export const FLOW_TIMEOUT_MS = 5000;

export function onboardingMark(): OnboardingMark | null {
  try {
    const raw = localStorage.getItem(MARK_KEY);
    return raw && MARKS.includes(raw) ? (raw as OnboardingMark) : null;
  } catch { return null; }
}

let launchMark: OnboardingMark | null | undefined;
/** Отметка онбординга с прошлых запусков: читается один раз — при старте приложения (App), до записи этого запуска.
 *  По ней тур по приложению (HQ-2338) отличает нового человека от того, кто уже пользуется. */
export function priorOnboardingMark(): OnboardingMark | null {
  if (launchMark === undefined) launchMark = onboardingMark();
  return launchMark;
}

export function setOnboardingMark(mark: OnboardingMark): void {
  try { localStorage.setItem(MARK_KEY, mark); } catch { /* приватный режим — до перезапуска покажем ещё раз */ }
}

/** Сброс после удаления аккаунта (HQ-2403): отметки нет — следующий запуск как первый (онбординг, затем весь тур).
 *  Отметка «с прошлых запусков» этого запуска тоже обнуляется: человек теперь новый и до перезапуска. */
export function resetOnboardingMark(): void {
  launchMark = null;
  try { localStorage.removeItem(MARK_KEY); } catch { /* нет localStorage — стирать нечего */ }
}

/* ── Согласие с политикой (щит «Privacy & data», 387:35103) ── */

export const POLICY_KEY = 'cricket.policy';
export const POLICY_VERSION = 1;
/** Адрес по url_key сервера: privacy_policy — политика Cricket; незнакомый ключ — null (кнопку не рисуем). */
export const PRIVACY_URL = 'https://activeplaylabs.com/legal/privacy/cricket';
const URLS: Record<string, string> = { privacy_policy: PRIVACY_URL };
export const urlOfKey = (key: string | undefined): string | null => (key ? URLS[key] ?? null : null);

/** Отметка есть и её версия не ниже текущей — щит политики пропускаем. */
export function policyAccepted(): boolean {
  try {
    const raw = localStorage.getItem(POLICY_KEY);
    const mark = raw ? JSON.parse(raw) as { v?: unknown; at?: unknown } : null;
    return Boolean(mark) && typeof mark!.v === 'number' && mark!.v >= POLICY_VERSION && typeof mark!.at === 'string';
  } catch { return false; }
}

export function acceptPolicy(now: Date = new Date()): void {
  try { localStorage.setItem(POLICY_KEY, JSON.stringify({ v: POLICY_VERSION, at: now.toISOString() })); } catch { /* приватный режим */ }
}

/* ── Ворота показа (без изменений с HQ-1092 / HQ-1797) ── */

/** Запуск, который онбординг не перебивает: глубокая ссылка (startapp / dev ?route=) или вход сразу в мир игры. */
export const launchAllowsOnboarding = (p: { startParam: string | null | undefined; toWorld: boolean }): boolean =>
  !p.startParam && !p.toWorld;

/** offline — сессия есть, а набор на паузе без сети: «Нет сети» с «Retry» вместо вечного скелетона (HQ-1797). */
export type GateDecision = 'show' | 'skip' | 'wait' | 'offline';

/** Показывать ли онбординг на этом запуске: нет отметки И избранное пустое, запуск обычный. Сессии нет — аккаунта ещё
 *  нет и избранного тоже; сессия есть — ждём набор сервера (не пришёл совсем — не держим человека, пропускаем; запрос
 *  на паузе без сети — offline, сеть вернётся — решение по тем же правилам). */
export function gateDecision(p: {
  launch: boolean; mark: OnboardingMark | null; session: boolean; loaded: boolean; failed: boolean; paused?: boolean;
  favorites: FavoritesSet;
}): GateDecision {
  if (!p.launch || p.mark) return 'skip';
  if (!p.session) return 'show';
  if (p.failed) return 'skip';
  if (!p.loaded) return p.paused ? 'offline' : 'wait';
  return favoritesCount(p.favorites) > 0 ? 'skip' : 'show';
}

/** Подпись пропуска шага выбора: secondary сервера с action skip / next; иначе пропуска нет. */
export const stepSkipLabel = (step: PickStep): string | null =>
  (step.secondary && (step.secondary.action === 'skip' || step.secondary.action === 'next') ? step.secondary.label : null);

/** Заголовок списка в щите: list_title сервера, нет — title шага. */
export const listTitle = (step: PickStep): string => step.list_title || step.title;

/** Поиск в шаге (387:34311 / 34517 / 34841): фильтр по уже полученным items / groups, без учёта регистра, без новой
 *  ручки. Турниры: совпала страна — группа целиком, иначе — только совпавшие турниры. Пустой запрос — шаг как есть. */
export function filterStep(step: PickStep, query: string): PickStep {
  const q = query.trim().toLocaleLowerCase();
  if (!q) return step;
  const hit = (...texts: Array<string | null | undefined>) => texts.some((s) => (s ?? '').toLocaleLowerCase().includes(q));
  switch (step.entity_type) {
    case 'team': return { ...step, items: step.items.filter((i) => hit(i.name, i.code)) };
    case 'player': return { ...step, items: step.items.filter((i) => hit(i.name, i.team?.name)) };
    case 'league': return {
      ...step,
      groups: step.groups
        .map((g) => (hit(g.name) ? g : { ...g, leagues: g.leagues.filter((l) => hit(l.name, l.code)) }))
        .filter((g) => g.leagues.length > 0),
    };
  }
}

export const isEmptyStep = (step: PickStep): boolean =>
  (step.entity_type === 'league' ? step.groups.length === 0 : step.items.length === 0);

/** Имя игрока строкой 387:33288: первое слово сверху, остальное жирным снизу; одно слово — только жирная строка. */
export function splitName(name: string): [string, string] {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words.length > 1 ? [words[0]!, words.slice(1).join(' ')] : ['', words[0] ?? ''];
}

// ---- События воронки (словарь v1, группа onboarding): step_name — key шага сервера (welcome, policy, favorites_teams,
// …, paywall; место шагов выбора без ответа — steps), step_index — номер шага с 0, result — next / skip.
type StepInfo = { key: string; index: number; variant: string };
const params = (s: StepInfo) => ({ step_name: s.key, step_index: s.index, variant: s.variant || undefined, flow_source: FLOW_SOURCE });

export const trackStepView = (s: StepInfo) => track('onboarding_step_view', params(s));
export function trackStepComplete(s: StepInfo, result: 'next' | 'skip', selection?: number): void {
  track('onboarding_step_complete', { ...params(s), result, selection_count: selection });
}
export const trackComplete = (variant: string) => track('onboarding_complete', { variant: variant || undefined, flow_source: FLOW_SOURCE });
